// Helpers over the in-memory detail tree (EmbedDetailMap) a transaction
// editor buffers before an atomic batch save: child rows keyed by form_no,
// each row optionally carrying its own `__children`. Moved verbatim from the
// platform's useEmbedRecords / LivePreviewTab so the shared screen and the
// platform embeds run the SAME buffer semantics.

import type { EmbedDetailMap } from '../contracts'
import { rowIsTicked, type RowSelect } from '../types'

// ── `row_select` forms (candidates the user ticks) ───────────────────────

function selectKeyOf(row: Record<string, unknown>, rs: RowSelect): string | null {
  if (!rs.key) return null
  const v = row[rs.key]
  return v == null || String(v).trim() === '' ? null : String(v).trim()
}

/**
 * Apply a returned rows change to a `row_select` form: incoming rows are
 * CANDIDATES — they arrive unticked (ticked when `tickNew`), and one matching
 * a row already on the form (by `rs.key`) is not added again.
 *
 * Without `replace` (the refill when a saved record opens) the rows on the
 * form all stay. With `replace` (the criteria changed) the list is rebuilt:
 * unsaved rows go; a saved row that is still a candidate stays as it is
 * (ticked or not); a saved row that no longer is a candidate is marked
 * `_deleted` — hidden, and deleted by the save — and comes back if a later
 * change offers it again. Returns the SAME array when nothing changes.
 */
export function applySelectableRowsChange(
  rows: Array<Record<string, unknown>> | undefined,
  change: { rows?: Array<Record<string, unknown>>; replace?: boolean; unchanged?: boolean } | undefined,
  rs: RowSelect,
  stamp: (row: Record<string, unknown>) => Record<string, unknown> = (r) => r,
  tickNew = false,
): Array<Record<string, unknown>> {
  const current = rows || []
  if (!change || change.unchanged) return current
  let kept = current
  if (change.replace) {
    const offered = new Set((change.rows || []).map(r => selectKeyOf(r, rs)).filter((k): k is string => !!k))
    kept = current.filter(r => r._txn_id).map(r => {
      const k = selectKeyOf(r, rs)
      const still = !k || offered.has(k)
      if (still && r._deleted) {
        const { _deleted, ...rest } = r
        void _deleted
        return rest
      }
      return still || r._deleted ? r : { ...r, _deleted: true }
    })
  }
  const seen = new Set(kept.filter(r => !r._deleted)
    .map(r => selectKeyOf(r, rs)).filter((k): k is string => !!k))
  const incoming: Array<Record<string, unknown>> = []
  for (const r of change.rows || []) {
    const k = selectKeyOf(r, rs)
    if (k && seen.has(k)) continue
    if (k) seen.add(k)
    incoming.push(stamp({ ...r, [rs.column]: tickNew ? 'Y' : 'N' }))
  }
  if (!incoming.length && kept.length === current.length
      && kept.every((r, i) => r === current[i])) return current
  return [...kept, ...incoming]
}

/** The rows of a `row_select` form as they go to the save: an unticked new
 *  row is left out, an unticked saved row is deleted. */
export function selectableRowsForSave(
  rows: Array<Record<string, unknown>> | undefined, rs: RowSelect,
): Array<Record<string, unknown>> {
  const out: Array<Record<string, unknown>> = []
  for (const r of rows || []) {
    if (rowIsTicked(r, rs)) { out.push(r); continue }
    if (r._txn_id) out.push({ ...r, _deleted: true })
  }
  return out
}

// After an atomic batch save, flag every returned child row as saved (the
// whole tree is persisted) and recurse into grandchildren. `_localId` is
// preserved by the engine so React keys + the grid's row identity stay stable.
export function markChildrenSaved(tree: EmbedDetailMap): EmbedDetailMap {
  const out: EmbedDetailMap = {}
  for (const [fno, rows] of Object.entries(tree || {})) {
    out[fno] = (rows || []).map((row) => {
      const r: Record<string, unknown> = { ...row, _saved: true }
      if (r.__children) r.__children = markChildrenSaved(r.__children as EmbedDetailMap)
      return r
    })
  }
  return out
}

// On an atomic failure (nothing was saved) tag the single row named by the
// engine's scope `path` ([{form_no, index}, …] from header down) with the
// error so the child grid highlights the failing cell. Immutable update.
export function annotateRowError(
  details: EmbedDetailMap,
  path: Array<{ form_no: string; index: number }>,
  fieldErrors: Record<string, string>,
  message: string,
): EmbedDetailMap {
  if (!path.length) return details
  const [head, ...rest] = path
  const rows = details[head.form_no]
  if (!rows || !rows[head.index]) return details
  const nextRows = rows.slice()
  const target: Record<string, unknown> = { ...nextRows[head.index] }
  if (rest.length === 0) {
    target._error = message
    if (Object.keys(fieldErrors).length) target._fieldErrors = fieldErrors
  } else {
    target.__children = annotateRowError(
      (target.__children as EmbedDetailMap) || {}, rest, fieldErrors, message,
    )
  }
  nextRows[head.index] = target
  return { ...details, [head.form_no]: nextRows }
}

// Whether the in-memory detail buffer holds any change not yet committed:
// a never-saved row (no _txn_id), or a flagged edit/delete — at any depth.
export function bufferHasUnsaved(buf: EmbedDetailMap): boolean {
  for (const rows of Object.values(buf || {})) {
    for (const r of rows || []) {
      if (!r._txn_id || r._edited || r._deleted) return true
      if (r.__children && bufferHasUnsaved(r.__children as EmbedDetailMap)) return true
    }
  }
  return false
}

// The warning codes the user already OK'd in a detail row's editor, across the
// whole tree. A row keeps them in `_ack_warnings` (a control key both engines
// strip) so the header Save starts with them acknowledged: a warning is
// confirmed ONCE, on the line, never asked again for the same save.
export function acknowledgedWarnings(buf: EmbedDetailMap): string[] {
  const out = new Set<string>()
  const walk = (tree: EmbedDetailMap) => {
    for (const rows of Object.values(tree || {})) {
      for (const r of rows || []) {
        if (r._deleted) continue
        for (const c of (r._ack_warnings as string[] | undefined) || []) out.add(c)
        if (r.__children) walk(r.__children as EmbedDetailMap)
      }
    }
  }
  walk(buf)
  return Array.from(out)
}

// ── Applying a returned child-form change ────────────────────────────────

/** Mark a row as touched so the batch save rewrites it (only meaningful for
 *  an already-saved row; a new row is inserted anyway). */
function markEdited(row: Record<string, unknown>): Record<string, unknown> {
  return row._txn_id ? { ...row, _edited: true } : row
}

/**
 * Apply one form's `rows` / `replace` / `unchanged` change (an item_change,
 * row event or action result — ItemChangeFormChange) to that form's buffered
 * rows. Pure; returns the SAME array when nothing changes.
 *
 * - `unchanged`, or no `rows` and no `replace` → untouched;
 * - `rows` alone → appended (the historical add-rows contract);
 * - `replace` → every live row goes: a saved one is kept as `_deleted` (the
 *   batch save deletes it), an unsaved one is dropped; then `rows` are added
 *   (none = clear). Deleted rows are kept AHEAD of the new ones so the save
 *   deletes before it inserts — a rebuilt row may reuse the old key.
 *
 * `stamp` gives each new row its client identity (e.g. withLocalId).
 */
export function applyFormRowsChange(
  rows: Array<Record<string, unknown>> | undefined,
  change: { rows?: Array<Record<string, unknown>>; replace?: boolean; unchanged?: boolean } | undefined,
  stamp: (row: Record<string, unknown>) => Record<string, unknown> = (r) => r,
): Array<Record<string, unknown>> {
  const current = rows || []
  if (!change || change.unchanged) return current
  const incoming = (change.rows || []).map(r => stamp({ ...r }))
  if (!change.replace) {
    return incoming.length ? [...current, ...incoming] : current
  }
  const kept = current
    .filter(r => r._txn_id)
    .map(r => (r._deleted ? r : { ...r, _deleted: true }))
  return [...kept, ...incoming]
}

/** Whether a form change touches the rows at all (so the parent row must be
 *  marked edited / dirty). */
export function formChangeTouchesRows(
  change: { rows?: unknown[]; replace?: boolean; unchanged?: boolean } | undefined,
): boolean {
  return !!change && !change.unchanged && (!!change.replace || !!(change.rows && change.rows.length))
}

/**
 * Apply a result's `forms` map to ONE row's own children (`__children`), for
 * every form_no in `childFormNos`. Returns the new `__children` and whether
 * anything changed.
 */
export function applyFormsToChildren(
  children: EmbedDetailMap | undefined,
  forms: Record<string, { rows?: Array<Record<string, unknown>>; replace?: boolean; unchanged?: boolean }> | undefined,
  childFormNos: string[],
  stamp?: (row: Record<string, unknown>) => Record<string, unknown>,
): { children: EmbedDetailMap; changed: boolean } {
  const base: EmbedDetailMap = children || {}
  if (!forms) return { children: base, changed: false }
  let changed = false
  const next: EmbedDetailMap = { ...base }
  for (const fno of childFormNos) {
    const change = forms[fno]
    if (!formChangeTouchesRows(change)) continue
    next[fno] = applyFormRowsChange(base[fno], change, stamp)
    changed = true
  }
  return { children: changed ? next : base, changed }
}

/**
 * The row event `row_complete` should fire for a finished row? A new row
 * always does; an existing one only when one of `when_changed` differs from
 * its previous values (or always, when `when_changed` is not set).
 */
export function rowCompleteWanted(
  whenChanged: string[] | undefined,
  row: Record<string, unknown>,
  old: Record<string, unknown> | null | undefined,
): boolean {
  if (!old || !whenChanged || whenChanged.length === 0) return true
  const norm = (v: unknown) => (v === null || v === undefined ? '' : String(v))
  return whenChanged.some(col => norm(row[col]) !== norm(old[col]))
}

/** A row stripped of client bookkeeping for a row_complete payload. */
export function rowForEvent(row: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(row || {})) {
    if (k === '__children' || k.startsWith('_')) continue
    out[k] = v
  }
  return out
}

/** A row's child rows for a row_complete payload: live rows, data only. */
export function childrenForEvent(children: EmbedDetailMap | undefined): Record<string, Array<Record<string, unknown>>> {
  const out: Record<string, Array<Record<string, unknown>>> = {}
  for (const [fno, rows] of Object.entries(children || {})) {
    out[fno] = (rows || []).filter(r => !r._deleted).map(rowForEvent)
  }
  return out
}

// ── Row completions still to run before a save ───────────────────────────
//
// An inline grid finishes a row when focus LEAVES it; a user who edits the
// last line and clicks Save never left it, and a completion may still be in
// flight. Each grid registers a flusher; a host's Save calls
// flushRowCompletions() first and, when it reports work, re-runs itself on
// the next render so it reads the rebuilt rows.
/** Outcome of completing a row: it ran and was applied, there was nothing to
 *  do, or the function refused the row (its message is on the row). */
export type RowCompletionOutcome = 'ran' | 'skipped' | 'failed'

const rowCompletionFlushers = new Set<() => Promise<RowCompletionOutcome>>()

/** Register a grid's "complete every pending row now" hook. Returns the
 *  unregister function (call it on unmount). */
export function registerRowCompletionFlusher(fn: () => Promise<RowCompletionOutcome>): () => void {
  rowCompletionFlushers.add(fn)
  return () => { rowCompletionFlushers.delete(fn) }
}

/** Thrown by flushRowCompletions when a row could not be completed — the save
 *  must stop (the row shows the function's message). */
export class RowCompletionFailed extends Error {
  constructor() {
    super('A line could not be completed — fix the highlighted row before saving')
    this.name = 'RowCompletionFailed'
  }
}

/** Run every registered grid's pending row completions. Resolves true when at
 *  least one ran (the caller's state snapshot is then stale — re-read it after
 *  a render, e.g. with afterRender()); throws RowCompletionFailed when one was
 *  refused. */
export async function flushRowCompletions(): Promise<boolean> {
  if (rowCompletionFlushers.size === 0) return false
  const results = await Promise.all(Array.from(rowCompletionFlushers)
    .map(f => f().catch((): RowCompletionOutcome => 'failed')))
  if (results.includes('failed')) throw new RowCompletionFailed()
  return results.includes('ran')
}

/** Resolve after React has rendered and run effects for state set just now. */
export function afterRender(): Promise<void> {
  return new Promise(resolve => {
    const raf = (globalThis as { requestAnimationFrame?: (cb: () => void) => number }).requestAnimationFrame
    if (raf) raf(() => setTimeout(resolve, 0))
    else setTimeout(resolve, 16)
  })
}

// ── Loading a document's existing lines ──────────────────────────────────

/** Rows asked for per round-trip — the engine's own per-call ceiling. */
export const CHILD_ROWS_PAGE = 500
/** Safety stop for a runaway loop (a server that never reports `total`). */
export const CHILD_ROWS_MAX = 20000

export interface ChildRowsPageQuery {
  limit: number
  offset: number
  explicitLimit: boolean
  formNo: string
  parentPk: Record<string, unknown>
}

/**
 * Every saved row of one child form under one parent, fetched page by page.
 * A document's lines are edited and re-saved as a WHOLE, so loading only a
 * first page (it used to be a fixed 200) silently hid lines 201+ of a large
 * order or receipt. Pages until the server's `total` is reached, a page comes
 * back empty, or the safety cap is hit — never trusting a short page alone,
 * since a server may answer with a smaller page size than asked for.
 */
export async function listAllChildRows(
  list: (q: ChildRowsPageQuery) => Promise<{ rows?: unknown[]; total?: number }>,
  formNo: string,
  parentPk: Record<string, unknown>,
): Promise<Array<Record<string, unknown>>> {
  const out: Array<Record<string, unknown>> = []
  for (;;) {
    const res = await list({
      limit: CHILD_ROWS_PAGE, offset: out.length, explicitLimit: true, formNo, parentPk,
    })
    const rows = (res?.rows || []) as Array<Record<string, unknown>>
    out.push(...rows)
    const total = typeof res?.total === 'number' ? res.total : null
    if (rows.length === 0) break
    if (total != null ? out.length >= total : rows.length < CHILD_ROWS_PAGE) break
    if (out.length >= CHILD_ROWS_MAX) {
      if (typeof console !== 'undefined') {
        console.warn(`child form ${formNo}: stopped loading at ${out.length} rows`)
      }
      break
    }
  }
  return out
}
